import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell } from 'lucide-react';
import { Button } from './ui/button';
import { newsAPI } from '../lib/api';
import { cn } from '../lib/utils';

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'только что';
  if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} дн назад`;
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

export default function NewsDropdown() {
  const [open, setOpen] = useState(false);
  const [news, setNews] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const dropdownRef = useRef(null);

  const loadNews = useCallback(async () => {
    try {
      const res = await newsAPI.getAll();
      setNews(res.data.news || []);
      setUnreadCount(res.data.unread_count || 0);
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadNews();
  }, [loadNews]);

  // Background polling every 2 min (only when tab is visible & dropdown closed)
  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden && !open) {
        loadNews();
      }
    }, 120000);
    return () => clearInterval(interval);
  }, [loadNews, open]);

  // Refresh on tab focus (with 5s throttle to avoid rapid re-fetches)
  useEffect(() => {
    let lastCheck = Date.now();
    const onVisibility = () => {
      if (!document.hidden && Date.now() - lastCheck > 5000) {
        lastCheck = Date.now();
        loadNews();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [loadNews]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleToggle = async () => {
    const wasOpen = open;
    setOpen(!wasOpen);
    if (!wasOpen && unreadCount > 0) {
      // Mark all as read when opening
      try {
        await newsAPI.markRead();
        setUnreadCount(0);
        setNews(prev => prev.map(n => ({ ...n, is_read: true })));
      } catch {}
    }
  };

  if (!loaded) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleToggle}
        className="relative"
        data-testid="news-bell-btn"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white animate-pulse">
            {unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 max-h-[70vh] bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-border bg-muted/30">
            <h3 className="text-sm font-semibold text-foreground">Новости проекта</h3>
            <p className="text-xs text-muted-foreground">Обновления и улучшения YaDela</p>
          </div>

          {/* News list */}
          <div className="overflow-y-auto flex-1">
            {news.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Пока нет новостей</p>
              </div>
            ) : (
              news.map((item) => (
                <div
                  key={item.id}
                  className={cn(
                    'px-4 py-3 border-b border-border/50 last:border-0 transition-colors',
                    !item.is_read && 'bg-primary/5'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-lg shrink-0 mt-0.5">{item.emoji || '📢'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground leading-tight">
                          {item.title}
                        </span>
                        {!item.is_read && (
                          <span className="h-2 w-2 rounded-full bg-primary shrink-0" />
                        )}
                      </div>
                      {item.content && (
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed line-clamp-3">
                          {item.content}
                        </p>
                      )}
                      <p className="text-[10px] text-muted-foreground/60 mt-1.5">
                        {timeAgo(item.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
